
import AppBar from 'material-ui/AppBar';
import MenuItem from 'material-ui/MenuItem';
import FlatButton from 'material-ui/FlatButton';
import FontIcon from 'material-ui/FontIcon';
import IconButton from 'material-ui/IconButton';
import TextField from 'material-ui/TextField';
import Divider from 'material-ui/Divider';
import Drawer from 'material-ui/Drawer';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { logo, logoWidth, useSSO } from 'config';
import { FormattedMessage } from 'react-intl';
import findIndex from 'lodash/findIndex';
import { useMsal } from '@azure/msal-react';
import { filter, get } from 'lodash';

import pallet from 'constants/colors';
import getAppraisalTitle from '../containers/utils/getAppraisalTitle';

const styles = {
  title: {
    cursor: 'pointer',
    fontSize: 24,
    fontWeight: 100,
  },
  logo: {
    cursor: 'pointer',
    backgroundImage: `url(${logo})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    height: 50,
    minWidth: logoWidth,
  },
  link: {
    color: '#fff',
    textDecoration: 'none',
  },
  dropdownColor: {
    color: '#000',
    textDecoration: 'none',
    fontWeight: 300,
  },
  menu: {
    height: 48,
    marginTop: 0,
    minWidth: 60,
  },
  menuIcon: {
    color: pallet.appBarTextColor,
  },
  menuLabel: {
    fontWeight: 400,
    fontSize: 16,
    color: pallet.appBarTextColor,
  },
  searchFieldInput: {
    color: pallet.appBarTextColor,
  },
  searchFieldInputHint: {
    color: pallet.appBarTextColorHint,
  },
  searchField: {
    width: 150,
  },
  appBar: {
    backgroundColor: pallet.appBarColor,
    paddingLeft: 16,
    paddingRight: 16,
    position: 'fixed',
    zIndex: 10000,
  },
  drawer: {
    paddingTop: 64,
  },
};

// IDEA: Add Badge to route to notify user about unfinished items
const routes = [
  { iconName: 'person', to: { pathname: '/me' }, title: 'toolbar.profile' },
  {
    iconName: 'school', to: { pathname: '/events' }, title: 'toolbar.courses', ID: 'Learning',
  },
  {
    iconName: 'search', to: { pathname: '/search' }, title: 'toolbar.search', ID: 'Search',
  },
  {
    iconName: 'account_balance', to: { pathname: '/pay' }, title: 'toolbar.payroll', ID: 'Payroll',
  },
  {
    iconName: 'beach_access', to: { pathname: '/holiday' }, title: 'toolbar.holiday', ID: 'Holiday',
  },
  {
    iconName: 'event_available', to: { pathname: '/vacations' }, title: 'holiday.vacationApprovals', ID: 'VacationApprovals', autoHide: true,
  },
  {
    iconName: 'view_list', to: { pathname: '/approvals' }, title: 'dash.title.approvals', ID: 'ApprovalRequests', autoHide: true,
  },
  {
    iconName: 'local_atm', to: { pathname: '/payslip' }, title: 'toolbar.payslip', ID: 'PaySlip',
  },
  {
    iconName: 'request_quote', to: { pathname: '/paytickets' }, title: 'dash.title.paytickets', ID: 'PayTicket', autoHide: true,
  },
  {
    iconName: 'question_answer', to: { pathname: '/appraisals' }, title: 'toolbar.appraisal', ID: 'PM',
  },
  {
    iconName: 'folder_open', to: { pathname: '/files' }, title: 'toolbar.repository', ID: 'Repository',
  },
  {
    iconName: 'edit', to: { pathname: '/agreements' }, title: 'toolbar.agreements', ID: 'Agreements',
  },
  {
    iconName: 'fitness_center', to: { pathname: '/benefits' }, title: 'toolbar.benefits', ID: 'Benefits',
  },
  {
    iconName: 'receipt', to: { pathname: '/grants' }, title: 'toolbar.grants', ID: 'Grants',
  },
  {
    iconName: 'directions_bike', to: { pathname: '/transportation' }, title: 'toolbar.transportationGrants', ID: 'TransportationGrants',
  },
  {
    iconName: 'device_hub', to: { pathname: '/orgchart' }, title: 'toolbar.orgchart', ID: 'OrgChart',
  },
  {
    iconName: 'coffee', to: { pathname: '/teria' }, title: 'teria.title', ID: 'Teria', autoHide: true,
  },
  {
    iconName: 'schedule', to: { pathname: '/time' }, title: 'time.title', ID: 'Time', autoHide: true,
  },
  {
    iconName: 'group', to: { pathname: '/inOut' }, title: 'inOut.title', ID: 'InOut', autoHide: true,
  },
  {
    iconName: 'settings', to: { pathname: '/settings' }, title: 'toolbar.settings', ID: 'Settings',
  },
];
const searchRoute = query => ({ pathname: '/search', query: { query } });

const LogoutItem = ({ router, onClose }) => {
  const { instance } = useMsal();
  const handleLogout = () => {
    onClose();
    if (useSSO) {
      instance.logout();
    } else {
      router.push({ pathname: '/account', query: { logout: true } });
    }
  };

  return (
    <MenuItem
      leftIcon={<FontIcon color={pallet.errorColor} className="material-icons">exit_to_app</FontIcon>}
      onClick={handleLogout}
      style={styles.dropdownColor}
    >
      <FormattedMessage id="toolbar.logout" />
    </MenuItem>
  );
};

LogoutItem.propTypes = {
  router: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

class Toolbar extends Component {

  constructor(props) {
    super(props);
    this.state = {
      open: false,
      searchQuery: '',
    };
  }

  handleClose = () => this.setState({ open: false });
  handleOpen = () => this.setState({ open: true });
  handleSearch = () => {
    if (this.state.searchQuery) {
      this.context.router.push(searchRoute(this.state.searchQuery));
      this.setState({ searchQuery: '' });
    }
  }
  handleQueryChange = ev => this.setState({ searchQuery: ev.target.value });
  handleEnter = ({ keyCode }) => keyCode === 13 && this.handleSearch();

  // BUG: Keyboard not hiding after search on iPad/Mobile
  render() {
    const {
      props: {
        isAuthenticated, valuesByKey, language, requestLanguage,
      }, context: { router }, state: { searchQuery },
    } = this;
    const { path } = this.props.routes.slice(-1)[0];
    if (!isAuthenticated) {
      return <div />;
    }
    if (valuesByKey) {
      const useEvents = get(valuesByKey, ['Use.Events', 'Value']) === 'true';
      if (useEvents) {
        const idx = findIndex(routes, ['ID', 'Learning']);
        routes[idx].title = 'toolbar.events';
        routes[idx].iconName = 'event';
      }
      if (get(valuesByKey, ['Appraisal.Title'])) {
        const i = findIndex(routes, ['ID', 'PM']);
        routes[i].title = getAppraisalTitle(valuesByKey);
      }
      // show correct title if store or cafeteria is not in use
      const hideStore = get(valuesByKey, ['Hide.Teria.Store', 'Value']) === 'true';
      const hideCafeteria = get(valuesByKey, ['Hide.Teria.Cafeteria', 'Value']) === 'true';
      const teriaIndex = findIndex(routes, ['ID', 'Teria']);
      if (hideStore && !hideCafeteria) {
        routes[teriaIndex].title = 'teria.cafeteria';
      } else if (!hideStore && hideCafeteria) {
        routes[teriaIndex].title = 'teria.store';
      }
    }

    return (
      <div className="toolbarContainer no-print">
        <AppBar
          style={styles.appBar}
          titleStyle={{ display: 'flex', alignItems: 'center' }}
          title={
            <div style={styles.logo} />
          }
          onTitleClick={() => router.push({ pathname: '/' })}
          showMenuIconButton={false}
          iconElementRight={
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 8 }}>
              {path !== 'search' && !(valuesByKey && valuesByKey['Hide.Search'] && valuesByKey['Hide.Search'].Value === 'true') &&
                <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    id="searchField"
                    hintText={<FormattedMessage id="toolbar.searchField" />}
                    value={searchQuery}
                    style={styles.searchField}
                    inputStyle={styles.searchFieldInput}
                    hintStyle={styles.searchFieldInputHint}
                    onKeyDown={this.handleEnter}
                    onChange={this.handleQueryChange}
                  />
                  <IconButton iconStyle={styles.menuIcon} onClick={this.handleSearch} iconClassName="material-icons">search</IconButton>
                </div>
              }
              <FlatButton
                label={<FormattedMessage className="hide-mobile" id="toolbar.menu" />}
                labelPosition="before"
                labelStyle={styles.menuLabel}
                hoverColor="transparent"
                onClick={this.handleOpen}
                style={styles.menu}
                data-cy="menu-button"
                icon={
                  <FontIcon style={styles.menuIcon} className="material-icons">menu</FontIcon>
                }
              />
            </div>
          }
        />
        <Drawer
          docked={false}
          width={250}
          open={this.state.open}
          openSecondary
          onRequestChange={open => this.setState({ open })}
          containerStyle={styles.drawer}
        >
          {valuesByKey && filter(routes, ({ ID, autoHide }) =>
          (
            (!autoHide && get(valuesByKey, [`Hide.${ID}`, 'Value']) !== 'true')
            || (autoHide && get(valuesByKey, [`Show.${ID}`, 'Value']) === 'true')
          )).map(({
            to, title, divider, iconName,
          }, index) => (
            <div key={index}>
              {divider && <Divider />}
              <MenuItem
                leftIcon={iconName && <FontIcon className="material-icons">{iconName}</FontIcon>}
                onClick={() => { this.handleClose(); router.push(to); }}
                style={styles.dropdownColor}
              >
                <FormattedMessage id={title} />
              </MenuItem>
            </div>
          ))}
          <Divider />
          <LogoutItem router={router} onClose={this.handleClose} />
          <Divider />
          <MenuItem
            leftIcon={<FontIcon className="material-icons">language</FontIcon>}
            onClick={() => {
              this.handleClose();
              requestLanguage(language === 'en' ? 'is' : 'en');
            }}
            style={styles.dropdownColor}
          >
            {language === 'en' ? 'Íslenska' : 'English'}
          </MenuItem>
        </Drawer>
      </div>
    );
  }
}

Toolbar.contextTypes = {
  router: PropTypes.object.isRequired,
  muiTheme: PropTypes.object.isRequired,
};

Toolbar.propTypes = {
  isAuthenticated: PropTypes.bool,
  valuesByKey: PropTypes.object,
  routes: PropTypes.array,
  language: PropTypes.string,
  requestLanguage: PropTypes.func,
};

export default Toolbar;



// WEBPACK FOOTER //
// ./src/components/Toolbar.js